import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { Monogram } from "./Monogram";
import { useAuth } from "../context/AuthContext";

const linkClass = ({ isActive }) =>
  `px-1 py-1 font-sans text-sm transition-colors border-b-2 ${
    isActive ? "text-ink border-brass" : "text-stone border-transparent hover:text-ink"
  }`;

export function AppHeader({ links, base, label, testidPrefix }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 bg-paper/95 backdrop-blur border-b border-hairline">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid={`${testidPrefix}-home-logo`}>
            <Monogram size={40} />
            <div className="hidden sm:block">
              <div className="font-serif text-lg text-ink leading-none" style={{ fontWeight: 600 }}>
                Working Actor
              </div>
              <div className="overline" style={{ fontSize: "0.55rem" }}>
                {label}
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={linkClass}
                data-testid={`${testidPrefix}-nav-${l.key}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline font-sans text-xs text-stone">{user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-ghost text-xs"
              data-testid={`${testidPrefix}-logout`}
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Sign out</span>
            </button>
            <button
              className="md:hidden text-ink"
              onClick={() => setOpen((v) => !v)}
              data-testid={`${testidPrefix}-menu-toggle`}
              aria-label="Menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>

        {open && (
          <nav className="md:hidden flex flex-col gap-1 pb-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-2 py-2 font-sans text-sm ${isActive ? "text-ink bg-hairline/40" : "text-stone"}`
                }
                data-testid={`${testidPrefix}-mnav-${l.key}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}

const STUDENT_LINKS = [
  { to: "/", key: "home", label: "Home", end: true },
  { to: "/portal", key: "dashboard", label: "Dashboard", end: true },
  { to: "/portal/sides", key: "sides", label: "My Sides" },
  { to: "/portal/classes", key: "classes", label: "Classes" },
  { to: "/portal/requests", key: "requests", label: "Requests" },
  { to: "/portal/billing", key: "billing", label: "Billing" },
  { to: "/portal/profile", key: "profile", label: "Profile" },
];

export default function PortalLayout() {
  return (
    <div className="min-h-screen bg-paper">
      <AppHeader links={STUDENT_LINKS} label="Student Portal" testidPrefix="portal" />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12">
        <Outlet />
      </main>
    </div>
  );
}
